/**
 * @module app.shared
 */

import { LookupFunctions, Parser, ViewFactory, ViewResources, autoinject, createOverrideContext, metadata } from 'aurelia-framework';

export type ActualViewFactory = ViewFactory & { template: DocumentFragment };

// TODO [Matt] Improve the definition.
export type EnhancedElement = HTMLElement & { au: any };

export function getComponentViewModel<T>(element: Element, controller: string = 'controller'): T | null {
    return (element as EnhancedElement).au?.[controller]?.viewModel || null;
}

export function getSlotContentFromViewFactory(viewFactory: ViewFactory): Element | null {
    return (viewFactory as ActualViewFactory).template.querySelector('au-content') as Element || null;
}

/**
 * Retrieves the custom attribute name associated with a class.
 * @argument {Constructor} target - The class or constructor for the class.
 * @returns {string | null} - The associated custom attribute name, or null if none present.
 */
export function getCustomAttributeName(target: Function): string | null {
    const result = metadata.getOwn(metadata.resource, target);
    if(result && Reflect.has(result, 'attributeName')) {
        return Reflect.get(result, 'attributeName');
    }
    const match = /(\w+)CustomAttribute/.exec(target.name);
    if(match && match[1]) {
        return match[1];
    }
    return null;
}

/**
 * Evaluate a JavaScript or Aurelia Binding expression against a binding context.
 * This is primarily used to evaluate View expressions in the View Model.
*/
@autoinject
export class ExpressionEvaluator {

    protected lookupFunctions: LookupFunctions;

    constructor(protected readonly parser: Parser, protected readonly resources: ViewResources) {
        this.lookupFunctions = (resources as ViewResources & { lookupFunctions: LookupFunctions }).lookupFunctions;
    }

    /**
     * Evaluates the JavaScript or Aurelia Binding expression against the specified context.
     * @param bindingContext The context against which expressions should be evaluated.
     * @param overrideContext The override context against which expressions should be evaluated.
     * @returns The result from evaluating the expression
     * @example const value = evaluator.evaluate('foo.bar === baz | test', { foo: { bar: 'foobar' }, { baz: 'it works' } });
     */
    public evaluate(text: string, bindingContext: any, overrideContext?: any): any {
        const expression = this.parser.parse(text);
        const scope = {
            bindingContext: bindingContext || {},
            overrideContext: overrideContext || createOverrideContext(bindingContext)
        };
        return expression.evaluate(scope, this.lookupFunctions);
    }
}
